import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppDispatch } from "app/hooks";
import {
  setCustomMessage,
  setOpen
} from "components/action-snackbar/action-snackbar-slice";
import {
  fetchCurrentUser,
  setCurrentClinic,
  setCurrentRegion,
  setToken
} from "features/auth/auth-slice";
import { emptySplitApi } from "services/empty-split-api";
import { SwitchPracticeResponse } from "services/types/practices.type";
import { getAuthHeaderFromStorage } from "utils/auth-header.util";

// Switched sessions are Bearer JWTs. We persist like the SSO callback's
// keep-logged-in path so RequireAuth rehydrates the new session on reload.
const SWITCH_TOKEN_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const readAccessToken = (data: SwitchPracticeResponse): string | undefined =>
  data.access ?? data.token ?? data.auth_token;

/**
 * Drives a practice switch: mint a Bearer session, flip the auth scheme, drop
 * all practice-scoped state (clinic/region + the entire RTK Query cache),
 * repopulate the user for the new context, and navigate home.
 *
 * Uses a direct credentialed fetch (not RTK Query) because the switch response
 * sets the HttpOnly `refresh_token` cookie — `credentials: "include"` is
 * required for the browser to store it across the app-host/API-host split.
 *
 * No-op when switching into the already-active practice.
 */
export default function useSwitchPractice() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const switchToPractice = useCallback(
    async (practiceId: number, currentPracticeId?: number) => {
      if (practiceId === currentPracticeId) return; // no-op: already active
      setError(null);
      setIsLoading(true);

      // Surface failures: set local error state AND the global snackbar so a
      // failed switch is never silent.
      const fail = () => {
        const message = "Couldn't switch practice. Please try again.";
        setError(message);
        dispatch(setCustomMessage(message));
        dispatch(setOpen(true));
      };

      try {
        const response = await fetch(
          `${import.meta.env.VITE_SUNO_API}/auth/switch-practice/`,
          {
            method: "POST",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: getAuthHeaderFromStorage() ?? ""
            },
            body: JSON.stringify({ practice_id: practiceId })
          }
        );
        if (!response.ok) {
          fail();
          return;
        }
        const data: SwitchPracticeResponse = await response.json();
        const token = readAccessToken(data);
        if (!token) {
          fail();
          return;
        }

        // Persist + mirror the new Bearer session.
        localStorage.setItem("token", token);
        localStorage.setItem("authScheme", "Bearer");
        localStorage.setItem(
          "expiration",
          String(Date.now() + SWITCH_TOKEN_TTL_MS)
        );
        dispatch(setToken({ token, scheme: "Bearer" }));

        // The previous practice's clinic/region no longer apply; the boot init
        // effect re-selects a clinic for the new practice once clinics refetch.
        dispatch(
          setCurrentClinic({
            currentClinic: null,
            numberOfClinics: 0,
            hasRegions: false,
            hasServiceLocations: false
          })
        );
        dispatch(setCurrentRegion(null));

        // Drop every cached response — they were scoped to the old practice.
        dispatch(emptySplitApi.util.resetApiState());

        // Repopulate the user (practice, default_clinic, flags) for the new
        // context before landing on the home route.
        await dispatch(fetchCurrentUser({ token }));

        navigate("/");
      } catch {
        fail();
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, navigate]
  );

  return { switchToPractice, isLoading, error };
}
